import { WebsiteWithConnections } from '../../types/website';
import { ChangeEvent, useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../utils/dateUtils';
import { Button, Card, Dropdown, Icon, IconButton, Input, Popover } from '../ui';

/** Shared edit-form shape for the website detail panels */
export interface WebsiteEditForm {
  url: string;
  title: string;
  category: string;
  color: string;
  registrar: string;
  registrar_username: string;
  registrar_password: string;
  dns_host: string;
  nameservers: string[];
  hosting_provider: string;
  hosting_username: string;
  hosting_password: string;
  hosting_location: string;
  ip_address: string;
  frontend_platform: string;
  frontend_theme: string;
  frontend_username: string;
  frontend_password: string;
  search_console_access: string;
  build_status: string;
  deployment_status: string;
  indexing_status: string;
  last_content_update: string;
}

export function InfoField({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: '5px' }}>{label}</div>
      <div
        className={mono ? 't-num' : ''}
        style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        {children}
      </div>
    </div>
  );
}

export function EmptyValue({ children = 'Not specified' }: { children?: React.ReactNode }) {
  return <span style={{ color: 'var(--text-faint)' }}>{children}</span>;
}

interface WebsiteInfoPanelProps {
  website: WebsiteWithConnections | null;
  isEditMode: boolean;
  editForm: WebsiteEditForm;
  setEditForm: (val: WebsiteEditForm) => void;
  showCustomColor: boolean;
  setShowCustomColor: (val: boolean) => void;
  predefinedColors: { label: string; value: string }[];
  handleColorChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  formatUrl: (url: string) => string;
}

export default function WebsiteInfoPanel({
  website,
  isEditMode,
  editForm,
  setEditForm,
  showCustomColor,
  predefinedColors,
  handleColorChange,
  formatUrl
}: WebsiteInfoPanelProps) {
  const [categories, setCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const categoryBoxRef = useRef<HTMLDivElement>(null);

  const handleColorChangeValue = (val: string) => {
    handleColorChange({ target: { value: val } } as ChangeEvent<HTMLSelectElement>);
  };

  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Fetch all distinct categories when component mounts
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('websites')
          .select('category')
          .not('category', 'is', null)
          .order('category');

        if (error) throw error;

        // Extract unique categories and filter out nulls
        const uniqueCategories = Array.from(new Set(
          (data || []).map((item: { category: string | null }) => item.category).filter(Boolean)
        )) as string[];

        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  const filteredCategories = categories.filter(category =>
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSetToday = () => {
    const today = new Date();
    setEditForm({
      ...editForm,
      last_content_update: today.toISOString().split('T')[0]
    });
  };

  const handleSetTodayInViewMode = async () => {
    if (!website) return;

    setIsUpdating(true);
    try {
      // Set to noon to avoid timezone issues
      const today = new Date();
      today.setHours(12, 0, 0, 0);

      const { error } = await supabase
        .from('websites')
        .update({
          last_content_update: today.toISOString()
        })
        .eq('id', website.id);

      if (error) throw error;

      // Force a reload of the website data
      window.location.reload();
    } catch (err) {
      console.error('Error updating last content update:', err);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card eyebrow="Website Info">
      {isEditMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <Input
              label="URL*"
              icon="globe"
              value={editForm.url}
              onChange={(e) => setEditForm({ ...editForm, url: e.target.value })}
            />
            <Input
              label="Title"
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
            />
          </div>

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Last Content Update</div>
            <div className="flex items-center" style={{ gap: '8px' }}>
              <input
                type="date"
                className="ck-date-input"
                value={editForm.last_content_update || ''}
                onChange={(e) => {
                  // Ensure we just store the date part without timezone adjustments
                  setEditForm({ ...editForm, last_content_update: e.target.value });
                }}
                style={{ flex: 1 }}
              />
              <Button size="sm" variant="secondary" onClick={handleSetToday}>
                Today
              </Button>
            </div>
          </div>

          <div>
            <div className="t-eyebrow" style={{ marginBottom: '7px' }}>Category</div>
            {isAddingNewCategory ? (
              <div className="flex items-center" style={{ gap: '8px' }}>
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter new category..."
                  autoFocus
                  style={{ flex: 1 }}
                />
                <IconButton
                  icon="check"
                  size="sm"
                  variant="outline"
                  label="Save Category"
                  onClick={() => {
                    if (newCategory.trim()) {
                      setEditForm({ ...editForm, category: newCategory.trim() });
                      setCategories([...categories, newCategory.trim()]);
                      setNewCategory('');
                    }
                    setIsAddingNewCategory(false);
                  }}
                />
                <IconButton
                  icon="x"
                  size="sm"
                  variant="outline"
                  label="Cancel"
                  onClick={() => {
                    setIsAddingNewCategory(false);
                    setNewCategory('');
                  }}
                />
              </div>
            ) : (
              <div ref={categoryBoxRef} style={{ position: 'relative' }}>
                <Input
                  icon="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => {
                    // Delay to allow clicking on dropdown items
                    setTimeout(() => setIsSearchFocused(false), 200);
                  }}
                  placeholder={editForm.category || 'Search categories...'}
                />

                <Popover
                  open={isSearchFocused}
                  anchorEl={categoryBoxRef.current}
                  onClose={() => setIsSearchFocused(false)}
                  matchWidth
                  role="listbox"
                  style={{ maxHeight: '240px' }}
                >
                    <button
                      onClick={() => setIsAddingNewCategory(true)}
                      className="hover-wash pressable"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '7px 10px',
                        border: 'none',
                        background: 'transparent',
                        borderRadius: 'var(--radius-sm)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-ui)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--accent-text)',
                      }}
                    >
                      <Icon name="plus" size={13} />
                      Add New Category
                    </button>

                    {searchTerm && filteredCategories.length === 0 ? (
                      <div style={{ padding: '7px 10px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                        No matches found
                      </div>
                    ) : (
                      filteredCategories.map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setEditForm({ ...editForm, category });
                            setSearchTerm('');
                          }}
                          className="hover-wash pressable"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '7px 10px',
                            border: 'none',
                            background: editForm.category === category ? 'var(--surface-active)' : 'transparent',
                            borderRadius: 'var(--radius-sm)',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-ui)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-body)',
                          }}
                        >
                          <span style={{ flex: 1 }}>{category}</span>
                          {editForm.category === category && (
                            <Icon name="check" size={12} style={{ color: 'var(--accent-text)' }} />
                          )}
                        </button>
                      ))
                    )}
                </Popover>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-end" style={{ gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <Dropdown
                  label="Node Color"
                  options={predefinedColors}
                  value={showCustomColor ? 'custom' : editForm.color}
                  onChange={(e) => handleColorChangeValue(e.target.value)}
                  placeholder="Color"
                />
              </div>
              <div
                style={{
                  width: 'var(--control-md)',
                  height: 'var(--control-md)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-hairline)',
                  background: editForm.color,
                  flexShrink: 0,
                }}
              />
            </div>

            {showCustomColor && (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <div className="flex items-end" style={{ gap: '10px' }}>
                  <Input
                    label="Custom Color (Hex)"
                    value={editForm.color}
                    onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                    placeholder="#RRGGBB"
                    style={{ flex: 1 }}
                  />
                  <div
                    style={{
                      width: 'var(--control-md)',
                      height: 'var(--control-md)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-hairline)',
                      background: editForm.color,
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-5) var(--space-6)' }}>
          <InfoField label="URL" mono>
            {formatUrl(website?.url || '')}
            <a
              href={website?.url.match(/^https?:\/\//) ? website.url : `https://${website?.url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hover-nudge"
              data-dir="up"
              style={{ display: 'inline-flex', color: 'var(--accent-text)' }}
              title="Open Site"
            >
              <Icon name="arrow-up-right" size={13} />
            </a>
          </InfoField>

          <InfoField label="Title">
            {website?.title || <EmptyValue>-</EmptyValue>}
          </InfoField>

          <InfoField label="Category">
            {website?.category || <EmptyValue />}
          </InfoField>

          <InfoField label="Node Color" mono>
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: 'var(--radius-pill)',
                background: website?.color || '#d95636',
                flexShrink: 0,
              }}
            />
            {website?.color || '#d95636'}
          </InfoField>

          <InfoField label="Last Content Update" mono>
            {website?.last_content_update
              ? formatDate(website.last_content_update)
              : <EmptyValue />}
            <Button size="sm" variant="secondary" onClick={handleSetTodayInViewMode} disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Today'}
            </Button>
          </InfoField>

          <InfoField label="Added On" mono>
            {new Date(website?.created_at || '').toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </InfoField>
        </div>
      )}
    </Card>
  );
}
